import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const initialRole = sp.get("role") === "parent" ? "parent" : "student";

  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    grade_target: "gym_8",
    current_class: 5,
    subjects: ["czech"],
    school_name: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSubject = (s) =>
    set("subjects", form.subjects.includes(s) ? form.subjects.filter((x) => x !== s) : [...form.subjects, s]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const payload = { role, ...form };
    if (role === "parent") {
      delete payload.grade_target;
      delete payload.current_class;
      delete payload.subjects;
      delete payload.school_name;
    } else {
      payload.current_class = Number(payload.current_class);
    }
    const res = await register(payload);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Účet vytvořen, jdeme na to.");
      navigate("/");
    } else {
      setError(res.error || "Registrace selhala");
    }
  };

  return (
    <div className="min-h-screen bg-parchment flex flex-col">
      <div className="border-b border-stone">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 h-16 flex items-center">
          <Link to="/" className="font-serif text-2xl text-navy">Discendo</Link>
        </div>
      </div>
      <div className="flex-1 flex items-start justify-center px-6 py-16">
        <div className="w-full max-w-2xl">
          <p className="caption mb-6">Vytvoření účtu</p>
          <h1 className="font-serif text-4xl text-ink mb-2">
            Začněme <span className="font-serif-italic">krátkou registrací</span>.
          </h1>
          <p className="text-slate mb-10">Registrace je bez pozvánky. Teď můžeš procvičovat češtinu pro osmiletá gymnázia; další kategorie připravujeme.</p>

          <div className="mb-8">
            <Label className="caption">Jsem</Label>
            <RadioGroup value={role} onValueChange={setRole} className="flex gap-6 mt-3" data-testid="role-group">
              <Label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="student" data-testid="role-student" />
                <span className="text-base">Žák / žákyně</span>
              </Label>
              <Label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="parent" data-testid="role-parent" />
                <span className="text-base">Rodič</span>
              </Label>
            </RadioGroup>
          </div>

          {error && <p role="alert" className="border border-red-300 text-red-800 p-4 mb-6">{error}</p>}
          <form onSubmit={onSubmit} className="space-y-6" data-testid="register-form">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="caption">Jméno</Label>
                <Input id="first_name" value={form.first_name}
                  onChange={(e) => set("first_name", e.target.value)} required
                  data-testid="reg-first-name" className="bg-bone border-stone focus-visible:ring-navy" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="caption">Příjmení</Label>
                <Input id="last_name" value={form.last_name}
                  onChange={(e) => set("last_name", e.target.value)} required
                  data-testid="reg-last-name" className="bg-bone border-stone focus-visible:ring-navy" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="caption">E-mail</Label>
              <Input id="email" autoComplete="username" type="email" value={form.email}
                onChange={(e) => set("email", e.target.value)} required
                data-testid="reg-email" className="bg-bone border-stone focus-visible:ring-navy" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="caption">Heslo (min. 10 znaků)</Label>
              <Input id="password" autoComplete="new-password" type="password" value={form.password}
                onChange={(e) => set("password", e.target.value)} required minLength={10}
                data-testid="reg-password" className="bg-bone border-stone focus-visible:ring-navy" />
            </div>

            {role === "student" && (
              <div className="border-t border-stone pt-6 space-y-6">
                <p className="caption">Cíl přípravy</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="caption">Kam směřuješ?</Label>
                    <Select value={form.grade_target} onValueChange={(v) => set("grade_target", v)}>
                      <SelectTrigger data-testid="reg-grade-target" className="bg-bone border-stone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ss_4">4letá SŠ / gymnázium (9. třída)</SelectItem>
                        <SelectItem value="gym_6">6leté gymnázium (7. třída)</SelectItem>
                        <SelectItem value="gym_8">8leté gymnázium (5. třída)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="current_class" className="caption">Aktuální třída</Label>
                    <Input id="current_class" type="number" min={1} max={12} value={form.current_class}
                      onChange={(e) => set("current_class", e.target.value)}
                      data-testid="reg-current-class" className="bg-bone border-stone focus-visible:ring-navy" />
                  </div>
                </div>
                <div className="space-y-3">
                  <Label className="caption">Předměty</Label>
                  <div className="flex gap-6">
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={form.subjects.includes("math")}
                        onCheckedChange={() => toggleSubject("math")}
                        data-testid="reg-subj-math" />
                      <span>Matematika</span>
                    </Label>
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={form.subjects.includes("czech")}
                        onCheckedChange={() => toggleSubject("czech")}
                        data-testid="reg-subj-czech" />
                      <span>Český jazyk a literatura</span>
                    </Label>
                  </div>
                </div>
              </div>
            )}

            <Button type="submit" disabled={submitting}
              className="w-full bg-navy hover:bg-navy-dark text-parchment rounded-sm py-6"
              data-testid="register-submit-btn">
              {submitting ? "Vytvářím účet…" : "Vytvořit účet"}
            </Button>
          </form>

          <p className="mt-10 text-sm text-slate">
            Už máš účet?{" "}
            <Link to="/login" className="text-navy underline underline-offset-4">
              Přihlas se.
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
